import type { RetriggerInvoiceCheckRunMutationOptions } from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { UUID } from '~/types/common';

import { queryKeysInvoice } from '../queryKeys';

import { retriggerInvoiceCheckRun } from './retriggerInvoiceCheckRun';

/**
 * Mutation key of the manual check run for one invoice, so a component that
 * does not own the mutation can read its pending state with `useIsMutating`.
 *
 * @param invoiceId - Invoice identifier.
 * @returns The mutation key.
 */
export function getRetriggerInvoiceCheckRunMutationKey(invoiceId: UUID) {
  return ['retriggerInvoiceCheckRun', { invoiceId }] as const;
}

/**
 * Mutation hook that re-runs the checks of an invoice.
 *
 * Invalidates detail, timeline, and V2 list queries before settlement, so the
 * mutation stays pending until the refetched detail carries the new run.
 *
 * @param invoiceId - Invoice identifier.
 * @param options - Optional TanStack Query mutation options.
 * @returns A mutation object; `mutate()` takes no variables.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/retrigger_check_run_invoices__invoice_id__check_run_post
 */
export function useMutationRetriggerInvoiceCheckRun(
  invoiceId: UUID,
  options?: RetriggerInvoiceCheckRunMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: () => retriggerInvoiceCheckRun(invoiceId),
    mutationKey: getRetriggerInvoiceCheckRunMutationKey(invoiceId),
    async onSettled(data, error, variables, onMutateResult, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeysInvoice.getDetail(invoiceId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysInvoice.getTimeline(invoiceId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysInvoice.getAllV2({}),
        }),
      ]);

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
}
